import { createTheme, alpha } from "@mui/material/styles";

// Brand tokens
const brand = {
  navy: "#0E4664",
  navyDark: "#0A3650",
  navyLight: "#1B5A7F",
  green: "#1B7A63",
  greenDark: "#146149",
  greenLight: "#2E9C82",
};

// Neutral scale tuned to pair with the brand navy/green
const neutral = {
  50: "#F7F8FA",
  100: "#F1F3F6",
  200: "#E4E7EC",
  300: "#D0D5DD",
  400: "#98A2B3",
  500: "#667085",
  600: "#475467",
  700: "#344054",
  800: "#1D2939",
  900: "#0F172A",
};

const fontFamily = [
  "ui-sans-serif",
  "system-ui",
  "sans-serif",
  '"Apple Color Emoji"',
  '"Segoe UI Emoji"',
  '"Segoe UI Symbol"',
  '"Noto Color Emoji"',
].join(", ");

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: brand.navy,
      dark: brand.navyDark,
      light: brand.navyLight,
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: brand.green,
      dark: brand.greenDark,
      light: brand.greenLight,
      contrastText: "#FFFFFF",
    },
    success: { main: "#12B76A", light: "#32D583", dark: "#027A48" },
    warning: { main: "#F79009", light: "#FDB022", dark: "#B54708" },
    error: { main: "#D92D20", light: "#F04438", dark: "#B42318" },
    info: { main: "#0BA5EC", light: "#36BFFA", dark: "#0086C9" },
    background: {
      default: neutral[50],
      paper: "#FFFFFF",
    },
    text: {
      primary: neutral[900],
      secondary: neutral[600],
      disabled: neutral[400],
    },
    divider: neutral[200],
    grey: {
      50: neutral[50],
      100: neutral[100],
      200: neutral[200],
      300: neutral[300],
      400: neutral[400],
      500: neutral[500],
      600: neutral[600],
      700: neutral[700],
      800: neutral[800],
      900: neutral[900],
    },
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    fontFamily,
    fontSize: 14,
    htmlFontSize: 16,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    h1: { fontSize: "2rem", fontWeight: 600, letterSpacing: "-0.02em", lineHeight: 1.2 },
    h2: { fontSize: "1.625rem", fontWeight: 600, letterSpacing: "-0.015em", lineHeight: 1.25 },
    h3: { fontSize: "1.375rem", fontWeight: 600, letterSpacing: "-0.01em", lineHeight: 1.3 },
    h4: { fontSize: "1.175rem", fontWeight: 600, letterSpacing: "-0.005em", lineHeight: 1.35 },
    h5: { fontSize: "1rem", fontWeight: 600, lineHeight: 1.4 },
    h6: { fontSize: "0.9375rem", fontWeight: 600, lineHeight: 1.4 },
    subtitle1: { fontSize: "0.9375rem", fontWeight: 500, lineHeight: 1.5 },
    subtitle2: { fontSize: "0.8125rem", fontWeight: 600, lineHeight: 1.5, letterSpacing: "0.02em" },
    body1: { fontSize: "0.875rem", lineHeight: 1.55 },
    body2: { fontSize: "0.8125rem", lineHeight: 1.5 },
    button: { textTransform: "none", fontWeight: 500, letterSpacing: "0.01em" },
    caption: { fontSize: "0.75rem", lineHeight: 1.4, color: neutral[600] },
    overline: {
      fontSize: "0.6875rem",
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
    },
  },
  // Softer, modern shadow stack (24 slots required by MUI)
  shadows: [
    "none",
    "0 1px 2px 0 rgba(16, 24, 40, 0.05)",
    "0 1px 3px 0 rgba(16, 24, 40, 0.08), 0 1px 2px 0 rgba(16, 24, 40, 0.04)",
    "0 2px 6px -1px rgba(16, 24, 40, 0.08), 0 1px 3px 0 rgba(16, 24, 40, 0.04)",
    "0 4px 8px -2px rgba(16, 24, 40, 0.08), 0 2px 4px -2px rgba(16, 24, 40, 0.04)",
    "0 6px 12px -2px rgba(16, 24, 40, 0.08), 0 3px 6px -3px rgba(16, 24, 40, 0.05)",
    "0 8px 16px -4px rgba(16, 24, 40, 0.08), 0 4px 8px -4px rgba(16, 24, 40, 0.04)",
    "0 12px 20px -4px rgba(16, 24, 40, 0.10), 0 4px 8px -4px rgba(16, 24, 40, 0.04)",
    "0 16px 24px -6px rgba(16, 24, 40, 0.12), 0 6px 10px -4px rgba(16, 24, 40, 0.04)",
    "0 20px 28px -8px rgba(16, 24, 40, 0.14), 0 8px 12px -6px rgba(16, 24, 40, 0.05)",
    "0 24px 32px -10px rgba(16, 24, 40, 0.16), 0 10px 14px -6px rgba(16, 24, 40, 0.05)",
    "0 28px 36px -10px rgba(16, 24, 40, 0.18), 0 12px 16px -8px rgba(16, 24, 40, 0.06)",
    "0 32px 40px -12px rgba(16, 24, 40, 0.18), 0 14px 18px -8px rgba(16, 24, 40, 0.06)",
    "0 36px 44px -12px rgba(16, 24, 40, 0.20), 0 16px 20px -10px rgba(16, 24, 40, 0.06)",
    "0 40px 48px -14px rgba(16, 24, 40, 0.20), 0 18px 22px -10px rgba(16, 24, 40, 0.06)",
    "0 44px 52px -14px rgba(16, 24, 40, 0.22), 0 20px 24px -12px rgba(16, 24, 40, 0.07)",
    "0 48px 56px -16px rgba(16, 24, 40, 0.22), 0 22px 26px -12px rgba(16, 24, 40, 0.07)",
    "0 52px 60px -16px rgba(16, 24, 40, 0.24), 0 24px 28px -14px rgba(16, 24, 40, 0.07)",
    "0 56px 64px -18px rgba(16, 24, 40, 0.24), 0 26px 30px -14px rgba(16, 24, 40, 0.08)",
    "0 60px 68px -18px rgba(16, 24, 40, 0.26), 0 28px 32px -16px rgba(16, 24, 40, 0.08)",
    "0 64px 72px -20px rgba(16, 24, 40, 0.26), 0 30px 34px -16px rgba(16, 24, 40, 0.08)",
    "0 68px 76px -20px rgba(16, 24, 40, 0.28), 0 32px 36px -18px rgba(16, 24, 40, 0.08)",
    "0 72px 80px -22px rgba(16, 24, 40, 0.28), 0 34px 38px -18px rgba(16, 24, 40, 0.09)",
    "0 76px 84px -22px rgba(16, 24, 40, 0.30), 0 36px 40px -20px rgba(16, 24, 40, 0.09)",
    "0 80px 88px -24px rgba(16, 24, 40, 0.30), 0 38px 42px -20px rgba(16, 24, 40, 0.09)",
  ] as any,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: neutral[50],
          color: neutral[900],
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
        },
        a: {
          color: brand.green,
          textDecorationColor: alpha(brand.green, 0.35),
        },
        "a:visited": {
          color: brand.green,
        },
        "*::-webkit-scrollbar": {
          width: 8,
          height: 8,
        },
        "*::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "*::-webkit-scrollbar-thumb": {
          background: neutral[300],
          borderRadius: 8,
          border: "2px solid transparent",
          backgroundClip: "padding-box",
        },
        "*::-webkit-scrollbar-thumb:hover": {
          background: neutral[400],
          backgroundClip: "padding-box",
          border: "2px solid transparent",
        },
      },
    },
    MuiLink: {
      defaultProps: {
        underline: "hover",
      },
      styleOverrides: {
        root: {
          color: brand.green,
          "&:visited": { color: brand.green },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          fontWeight: 500,
          padding: "6px 14px",
        },
        sizeSmall: {
          padding: "4px 10px",
          fontSize: "0.8125rem",
        },
        sizeLarge: {
          padding: "8px 18px",
          fontSize: "0.9375rem",
        },
        containedPrimary: {
          "&:hover": { backgroundColor: brand.navyDark },
        },
        containedSecondary: {
          "&:hover": { backgroundColor: brand.greenDark },
        },
        outlined: {
          borderColor: neutral[300],
          color: neutral[700],
          "&:hover": {
            borderColor: neutral[400],
            backgroundColor: alpha(neutral[500], 0.04),
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        rounded: { borderRadius: 12 },
        outlined: {
          border: `1px solid ${neutral[200]}`,
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${neutral[200]}`,
          backgroundImage: "none",
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: "inherit" },
      styleOverrides: {
        root: {
          backgroundColor: "#FFFFFF",
          color: neutral[900],
          borderBottom: `1px solid ${neutral[200]}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundImage: "none",
          borderRight: `1px solid ${neutral[200]}`,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: "small",
        variant: "outlined",
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: "#FFFFFF",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: neutral[300],
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: neutral[400],
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: brand.navy,
            borderWidth: 1.5,
          },
        },
        input: {
          padding: "9px 12px",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: neutral[600],
          "&.Mui-focused": { color: brand.navy },
        },
      },
    },
    MuiSelect: {
      defaultProps: { size: "small" },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
        sizeSmall: {
          height: 22,
          fontSize: "0.75rem",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: neutral[800],
          fontSize: "0.75rem",
          padding: "6px 10px",
          borderRadius: 6,
        },
        arrow: { color: neutral[800] },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${neutral[200]}`,
          fontSize: "0.8125rem",
        },
        head: {
          fontWeight: 600,
          color: neutral[700],
          backgroundColor: neutral[50],
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: neutral[200],
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 14,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderTopLeftRadius: 3,
          borderTopRightRadius: 3,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          minHeight: 44,
        },
      },
    },
  },
});

export default theme;
